/* ==========================================================================
   Pull down to refresh

   Asked for by Oster and, independently, by the first cleaner to use the app —
   which is about as clear a signal as feedback gets.

   Only engages when the list is already at the top, so it never fights an
   ordinary scroll. Touch only: on a desktop the data reloads on navigation
   anyway, and a mouse has no gesture for this.
   ========================================================================== */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../lib/i18n';

const TRIGGER = 68;   // how far to pull before it counts
const MAX = 96;       // how far the content will follow your finger

export function PullToRefresh({
  onRefresh, children,
}: { onRefresh: () => Promise<unknown>; children: ReactNode }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;

    /* The scrolling element is the screen body, which is our parent. */
    const scroller = el.closest('.screen') as HTMLElement | null;
    if (!scroller) return;

    let startY = 0;
    let active = false;

    const onStart = (e: TouchEvent) => {
      if (busy || scroller.scrollTop > 0) return;
      startY = e.touches[0].clientY;
      active = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!active) return;
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) { active = false; setPull(0); return; }
      /* Resistance, so it feels like pulling against something. */
      const eased = Math.min(MAX, delta * 0.5);
      setPull(eased);
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = async () => {
      if (!active) return;
      active = false;
      if (pullRef.current >= TRIGGER) {
        setBusy(true);
        setPull(TRIGGER);
        try { await onRefresh(); } finally {
          setBusy(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    scroller.addEventListener('touchstart', onStart, { passive: true });
    scroller.addEventListener('touchmove', onMove, { passive: false });
    scroller.addEventListener('touchend', onEnd);
    scroller.addEventListener('touchcancel', onEnd);
    return () => {
      scroller.removeEventListener('touchstart', onStart);
      scroller.removeEventListener('touchmove', onMove);
      scroller.removeEventListener('touchend', onEnd);
      scroller.removeEventListener('touchcancel', onEnd);
    };
  }, [busy, onRefresh]);

  /* The handler closes over `pull`; a ref keeps it current without rebinding. */
  const pullRef = useRef(0);
  pullRef.current = pull;

  const ready = pull >= TRIGGER;

  return (
    <div ref={wrap}>
      <div
        className="ptr"
        style={{ height: pull, opacity: pull > 8 ? 1 : 0 }}
        aria-hidden={pull === 0}
      >
        <span className={'ptr-spinner' + (busy ? ' spin' : '')} />
        <span className="ptr-label">
          {busy ? t('tx_refreshing') : ready ? t('tx_release') : t('tx_pull')}
        </span>
      </div>
      <div style={{ transform: `translateY(${busy ? 0 : pull * 0.35}px)` }}>
        {children}
      </div>
    </div>
  );
}
